import { KeyboardEvent, ReactElement } from "react";
import { IonButton, IonInput, IonItem, IonLabel } from "@ionic/react";
import { NumberQuestionType } from "../../../../types/Question";
import { useTranslation } from "react-i18next";
import { getDecimalStep } from "../../../../helper/number";
import { useConfig } from "../../../../contexts/ConfigContext";
import useVocalisation from "../../../../hooks/useVocalisation";
import SpeechToTextRow from "./SpeechToTextRow";

interface AnswerNumberProps {
  question: NumberQuestionType;
  value?: number;
  onChange: (value: string | null) => void;
  onEnter?: () => void;
}

const AnswerNumber = ({
  question,
  value = 0,
  onChange,
  onEnter,
}: AnswerNumberProps): ReactElement => {
  const { t } = useTranslation();

  const { selectedRole } = useConfig();

  const { startListnening, isListening } = useVocalisation([], (value) =>
    onChange(value)
  );

  const isSpeechRecEnabled =
    selectedRole?.attributes?.is_speech_recognition_enabled;
  const { detail } = question;

  const { min_value, max_value, number_decimal, prefix, suffix } = detail;

  // Identity phone question: a dedicated phone input — digits with an optional leading "+",
  // no min/max/step/decimals semantics, tel keyboard.
  const isPhone = question.identity_target === "phone_number";

  // A negative min (e.g. Base Excess in mmol/L) opens signed entry: the input accepts a leading
  // "-" and a ± toggle is shown, because the tel/decimal mobile keyboards have no minus key.
  const allowNegative = !isPhone && min_value < 0;

  const step: string = getDecimalStep(number_decimal).toString();
  const inputmode: "tel" | "decimal" = isPhone || number_decimal === 0 ? "tel" : "decimal";

  const handleChange = (e: CustomEvent): void => {
    if (isPhone) {
      // Keep digits and a single leading "+" only (pasted "06 12.34-56 78" → "0612345678").
      const phone = String(e.detail.value ?? "")
        .replace(/[^\d+]/g, "")
        .replace(/(?!^)\+/g, "");
      onChange(phone);
      return;
    }
    // Accept either decimal separator: normalize comma -> dot, then strip everything but digits and
    // dots (plus a single leading "-" on signed questions). Numbers are always stored dot-decimal,
    // so a French "12,5" is saved as 12.5 (not "125") and exports stay clean — a signed value
    // exports as e.g. "-3.5" in the same column. (The g flag strips ALL stray chars, e.g. "1 000 kg".)
    const raw = e.detail.value?.replace(/,/g, ".");
    const value = allowNegative
      ? raw?.replace(/[^0-9.-]/g, "").replace(/(?!^)-/g, "")
      : raw?.replace(/[^0-9.]/g, "");
    onChange(value);
  };

  // Flip the sign of the current value ("3.5" <-> "-3.5"). This is the only way to enter a
  // negative number on mobile keyboards; desktop users can also just type "-".
  const toggleSign = (): void => {
    const s = String(value ?? "");
    onChange(s.startsWith("-") ? s.slice(1) : `-${s}`);
  };

  // Enter acts as "Next" for the number field.
  const handleKeyDown = (e: KeyboardEvent): void => {
    if (e.key === "Enter") {
      e.preventDefault();
      onEnter?.();
    }
  };
  // NOTE: bound to onIonInput (per-keystroke), not onIonChange (fires on blur and is
  // dispatched async). With onIonChange, tapping "Next" right after typing races the
  // blur commit: getNextQuestion can read a stale/empty driver value, so a NUMBER that
  // drives a repeating set expands to 0 instances and the set is skipped entirely.

  return (
    <>
       <IonItem>
        {allowNegative && (
          <IonButton
            slot="start"
            fill="outline"
            onClick={toggleSign}
            aria-label={t("answer_number_toggle_sign")}
          >
            ±
          </IonButton>
        )}
        {!isPhone && prefix && (
          <IonLabel slot="start" color="medium">
            <p className="res-text-small">{prefix}</p>
          </IonLabel>
        )}
        <IonInput
          min={isPhone ? undefined : min_value.toString()}
          max={isPhone ? undefined : max_value.toString()}
          value={value}
          inputmode={inputmode}
          onIonInput={handleChange}
          onKeyDown={handleKeyDown}
          type="text"
          placeholder={t(isPhone ? "answer_phone_placeholder" : "answer_number_placeholder")}
          step={isPhone ? undefined : step}
          className="res-text-input"
        />
        {!isPhone && suffix && (
          <IonLabel slot="end" color="medium">
            <p className="res-text-small">{suffix}</p>
          </IonLabel>
        )}
      </IonItem>
      {isSpeechRecEnabled && (
        <SpeechToTextRow isListening={isListening} onStart={startListnening} lines="full" />
      )}
    </>
  );
};

export default AnswerNumber;
